import { useActionData, useOutletContext, useRouteLoaderData, useSearchParams } from 'react-router'

import { useCustomCssPreviewOverride } from './useCustomCssPreviewOverride'
import { useEntitlements } from './useEntitlements'
import { useLandingPage } from './useLandingPage'
import { ConsumerLoaderData, RootOutletContext } from '~/portal/types/portal'
import { SpPortalActionData } from '~/portal/types/preview'
import { CUSTOM_CSS_PREVIEW_QUERY_PARAM, CUSTOM_CSS_PREVIEW_QUERY_PARAM_VALUE, CUSTOM_CSS_PREVIEW_SESSION_MARKER } from '~/portal/utils/shared/customCssPreview'
import { mergeLandingPagePreviewData } from '~/portal/utils/shared/preview'
import { useDarkMode } from '~/shared/components/DarkMode/hook'

export const useConsumerBranding = () => {
  const { isDark } = useDarkMode()

  // if preview is set by param
  const { previewMode, portalSubdomain } = useRouteLoaderData<ConsumerLoaderData>('routes/_docs/_index') || {}
  const isPreviewParam = !!previewMode

  const [searchParams] = useSearchParams()
  const isCssPreview = searchParams.get(CUSTOM_CSS_PREVIEW_QUERY_PARAM) === CUSTOM_CSS_PREVIEW_QUERY_PARAM_VALUE
  const isCssPreviewSession = isCssPreview || (typeof window !== 'undefined' && sessionStorage.getItem(CUSTOM_CSS_PREVIEW_SESSION_MARKER) === CUSTOM_CSS_PREVIEW_QUERY_PARAM_VALUE)
  const previewCustomCss = useCustomCssPreviewOverride(portalSubdomain ?? '', isCssPreviewSession)
  const { sbFooterRemoved } = useEntitlements()
  const customCssEnabled = sbFooterRemoved

  // if preview is set for offline pages via an action
  const { isPreview: isOfflinePreview = false, previewData: offlinePreview = undefined } = useActionData<SpPortalActionData>() || {}

  // if preview is set for the outlet
  const { isPreview: isPreviewOutlet = false, previewData: outletPreview = undefined } = useOutletContext<RootOutletContext>() || {}

  const landingPageData = useLandingPage()

  const mergedPreviewData = (isPreviewParam || isPreviewOutlet || isOfflinePreview) ? mergeLandingPagePreviewData(landingPageData, outletPreview ?? offlinePreview) : landingPageData

  const { accentColor, 
    accentColorDarkMode, 
    name, 
    fontName, 
    heroImageHref, 
    heroImageDarkModeHref, 
    illustrationImageHref, 
    illustrationImageDarkModeHref, 
    logoImageHref, 
    logoImageDarkModeHref, 
    faviconHref, 
    faviconDarkModeHref,
    description,
    landingPageContent,
    footer,
    customCss } = mergedPreviewData

  const effectiveCustomCss = previewCustomCss ?? customCss
  // Accent colours belong to the default appearance; once custom CSS is in effect
  // (saved, or live in the preview tab) it takes precedence and the accent colour
  // must not also be applied, or it bleeds through custom-styled elements.
  const hasCustomCss = customCssEnabled && !!effectiveCustomCss

  const currentAccentColor = hasCustomCss ? undefined : (isDark() ? accentColorDarkMode : accentColor)

  const heroImage = isDark() ? heroImageDarkModeHref : heroImageHref

  const illustrationImage = isDark() ? illustrationImageDarkModeHref : illustrationImageHref

  const logoImage = isDark() ? logoImageDarkModeHref : logoImageHref

  const favicon = isDark() ? faviconDarkModeHref : faviconHref

  return {
    accentColor: currentAccentColor,
    accentColorLight: hasCustomCss ? undefined : accentColor,
    accentColorDark: hasCustomCss ? undefined : accentColorDarkMode,
    heroImage,
    illustrationImage,
    name,
    fontName,
    description,
    landingPageContent,
    logoImage,
    favicon,
    footer,
    customCss: effectiveCustomCss,
    hasCustomCss,
  }
}